/* eslint-disable react/jsx-no-constructed-context-values */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
// import 'antd/dist/antd.min.css';
import { EditUserContext, SelectedUsersContext } from 'pages/UserPage/context';

import AdminPage from 'pages/AdminPage';
import BulkAddUsersPage from 'pages/BulkAddUsersPage';
import UserPage from 'pages/UserPage';
import LoginPage from 'pages/LoginPage';
import PrivacyPolicyPage from 'pages/PrivacyPolicyPage';
import TermsOfUsePage from 'pages/TermsOfUsePage';
import Header from 'common/components/Header';
import {
  AttributesContext,
  SearchTextContext,
  LoggedInUserContext,
  SearchPaneResultsContext,
} from 'common/contexts';
import { plainFetch, simpleFetch } from 'common/utils/http';
import authTarget from 'common/utils/events';
import { UserData, PersonUserData } from 'common/constants/types';
import './App.css';

function App() {
  const [isLoading, setLoading] = useState(true);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [attributes, setAttributes] = useState<UserData>({ genres: [] });
  const [loggedInUser, setLoggedInUser] = useState<UserData>({ genres: [] });
  const [sortedSearchResults, setSortedSearchResults] = useState<PersonUserData[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [editUser, setEditUser] = useState(false);

  const searchTextContextValue = useMemo(
    () => ({
      searchText,
      setSearchText,
      showSearchInput,
      setShowSearchInput,
    }),
    [searchText, showSearchInput, setShowSearchInput]
  );

  authTarget.unique(
    'loggedOut',
    'app',
    () => {
      setLoggedIn(false);
      setSearchText('');
      setAttributes({ genres: [] });
    },
    {}
  );

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        // this fires our initial request to check whether the user is
        // logged in
        const response = await plainFetch('/api/v1/attributes');
        if (response.status === 200) {
          setLoggedIn(true);
          setAttributes(await response.json());
        }
        setLoading(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    const getLoggedInUser = async () => {
      try {
        const currentUserId = (await simpleFetch('/api/v1/current-user')).user_id;
        const currentUser = await simpleFetch(`/api/v1/people/${currentUserId}`);

        setLoggedInUser(currentUser);
      } catch (e) {
        console.error(e);
      }
    };

    fetchAttributes();
    getLoggedInUser();
  }, []);

  const logout = async () => {
    try {
      const response = await plainFetch('/api/v1/logout');
      if (response.status === 200) {
        authTarget.dispatch('loggedOut');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  let renderedContent: React.ReactNode;

  if (isLoading) {
    renderedContent = <div style={{ display: 'flex' }}>Loading</div>;
  } else if (!isLoggedIn) {
    renderedContent = (
      <Switch>
        <Route component={LoginPage} path="/login" />
        <Route path="/" render={() => <Redirect to="/login" />} />
      </Switch>
    );
  } else {
    renderedContent = (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Helmet>
          <title>{window.location.hostname}</title>
        </Helmet>
        <div style={{ display: 'flex', height: '100vh' }}>
          <Switch>
            <Route exact path="/" render={() => <Redirect to="/users" />} />
            <Route
              path="/logout"
              render={() => {
                logout();
                return null;
              }}
            />
            <Route path="/admin">
              <AdminPage />
            </Route>
            {/* TODO: refactor this to nest /users/bulk-add under /users */}
            <Route component={BulkAddUsersPage} path="/users/bulk-add" />
            <Route component={UserPage} path="/users" />
          </Switch>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AttributesContext.Provider value={{ attributes, setAttributes }}>
        <SearchPaneResultsContext.Provider value={{ sortedSearchResults, setSortedSearchResults }}>
          <SearchTextContext.Provider value={searchTextContextValue}>
            <SelectedUsersContext.Provider value={{ selectedUsers, setSelectedUsers }}>
              <EditUserContext.Provider value={{ editUser, setEditUser }}>
                <LoggedInUserContext.Provider value={{ loggedInUser, setLoggedInUser }}>
                  {/* Public legal pages stay reachable when logged out (app store + OAuth review). */}
                  <Switch>
                    <Route component={PrivacyPolicyPage} path="/privacy" />
                    <Route component={TermsOfUsePage} path="/terms" />
                    <Route render={() => renderedContent} />
                  </Switch>
                </LoggedInUserContext.Provider>
              </EditUserContext.Provider>
            </SelectedUsersContext.Provider>
          </SearchTextContext.Provider>
        </SearchPaneResultsContext.Provider>
      </AttributesContext.Provider>
    </Router>
  );
}

export default App;
